import React, { useEffect, useState } from "react";
import Box from "@material-ui/core/Box";
import { Button, Typography, Chip } from "@material-ui/core";
import { Redirect, useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import { useTranslate } from "react-polyglot";
import _ from "lodash";
import { useBaseData } from "../../Providers/Data/BaseDataProvider.jsx";
import useTitle from "../../hooks/useTitle.js";
import { useAuth } from "../../Providers/Auth/AuthProvider.jsx";
import { useLocale } from "../../Providers/i18n/I18nProvider.jsx";
import BaseAccordion from "../../SharedComponents/BaseAccordion.jsx";
import useCompanyPublicId from "../../hooks/useCompanyPublicId.js";
import useFavicon from "../../hooks/useFavicon.js";
import SubjectCard from "./SharedComponents/SubjectCard.jsx";
import LockDialog from "../../SharedComponents/LockDialog.jsx";
import CompanyLayout from "./SharedComponents/CompanyLayout.jsx";
import ViewportMeta from "../../SharedComponents/ViewportMeta.jsx";

const useStyles = makeStyles((theme) => ({
  mainBox: {
    width: "100%",
    backgroundColor: "#F1F2F2",
    minHeight: "100vh",
  },
  subjectsWrapper: {
    padding: "26px 20px",
    width: "80%",
    marginLeft: "auto",
    marginRight: "auto",
    [theme.breakpoints.up("lg")]: {
      paddingLeft: 0,
      paddingRight: 0,
    },
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },
  logo: {
    maxHeight: 150,
  },
  addedSubjects: {
    padding: "45px 0",
  },
  more: {
    backgroundColor: theme.palette.grey[300],
    borderRadius: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
  expandableList: {
    padding: 0,
    flexDirection: "column",
    backgroundColor: "#F1F2F2",
  },
  logoutButton: {
    paddingLeft: 20,
    paddingRight: 20,
    marginTop: 20,
  },
  chipBox: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    "& .MuiChip-root": {
      marginRight: theme.spacing(1),
    },
  },
  accordionBox: {
    marginTop: 15,
    "& .MuiPaper-root.MuiAccordion-root": {
      backgroundColor: "#F1F2F2",
    },
  },
}));

/** @returns {*}
 * @constructor
 */
const Subjects = () => {
  const { companyName, domain } = useCompanyPublicId();
  const classes = useStyles();
  const t = useTranslate();
  const { signOut, company2, user } = useAuth();
  const {
    availableSubjects,
    company,
    schoolNotFound,
    getAvailableSubjects,
    getAvailableSubjectsForCompany,
  } = useBaseData();
  const history = useHistory();
  const { locale } = useLocale();
  const [subjectsByLocale, setSubjectsByLocale] = useState([]);
  const [addedSubjects, setAddedSubjects] = useState([]);
  const [notAddedSubjects, setNotAddedSubjects] = useState([]);
  const [tags, setTags] = useState([]);
  const [currentTag, setCurrentTag] = useState("");

  const company_ = company || company2;

  useTitle("title.subjects", { school: (company_ && company_.name) || companyName || domain });

  useFavicon();

  useEffect(() => {
    if (user) {
      getAvailableSubjects().then();
    } else {
      getAvailableSubjectsForCompany(domain || companyName).then();
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // в зависимости от языка меняем отображение предметов
  useEffect(() => {
    if (availableSubjects && locale) {
      setSubjectsByLocale(availableSubjects.filter((subj) => subj.langs?.includes(locale)));
    }
  }, [availableSubjects, locale]);

  // собираем теги
  useEffect(() => {
    if (subjectsByLocale.length > 0) {
      const newTags = subjectsByLocale.flatMap((subj) => subj.tagsObj);
      const uniqTags = _.sortBy(_.uniqBy(newTags, "name"), "showOrder");
      if (uniqTags.length > 0) {
        setTags(uniqTags.map((tag) => tag.name));
      } else {
        setTags([]);
        setCurrentTag("");
      }
    }
  }, [subjectsByLocale]);

  // разделяем предметы на добавленные и не добавленные
  useEffect(() => {
    if (subjectsByLocale.length > 0) {
      if (currentTag) {
        const subjByCurrentTag = subjectsByLocale.filter((subj) => subj.tags.includes(currentTag));
        setAddedSubjects(subjByCurrentTag.filter((subj) => subj.added === true || subj.added === undefined));
        setNotAddedSubjects(subjByCurrentTag.filter((subj) => subj.added === false));
      } else {
        setAddedSubjects(subjectsByLocale.filter((subj) => subj.added === true || subj.added === undefined));
        setNotAddedSubjects(subjectsByLocale.filter((subj) => subj.added === false));
      }
    }
  }, [subjectsByLocale, currentTag]);

  /**
   * Обработчик нажатия на карточку предмета — выполняет переход на страницу предмета.
   *
   * @param {object} subject - Объект предмета, содержащий идентификатор.
   */
  const subjectEnterClickHandler = (subject) => {
    history.push(`${companyName ? "/" + companyName : ""}/subjects/${subject.id}`);
  };

  if (schoolNotFound) {
    return <Redirect to="/" />;
  }

  return (
    <Box className={classes.mainBox}>
      <ViewportMeta
        // Жёсткая фиксация масштаба страницы для мобильных браузеров
        initialScale={0.7}
      />
      <CompanyLayout>
        <Box className={classes.subjectsWrapper} style={{ justifyContent: "center" }}>
          <Typography align="center" gutterBottom>
            {t("chooseSubject")}
          </Typography>
          {tags.length > 0 && (
            <Box className={classes.chipBox}>
              <Chip label={t("all")} color={!currentTag ? "primary" : "default"} onClick={() => setCurrentTag("")} />
              {tags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  color={tag === currentTag ? "primary" : "default"}
                  onClick={() => setCurrentTag(tag)}
                />
              ))}
            </Box>
          )}
          {addedSubjects.map((subject) => (
            <SubjectCard key={subject.id} subject={subject} onClick={() => subjectEnterClickHandler(subject)} />
          ))}
          {notAddedSubjects.length > 0 && (
            <Box className={classes.accordionBox}>
              <BaseAccordion
                defaultExpanded
                summary={<Typography>{t("more")}</Typography>}
                summaryClassName={classes.more}
                detailsClassName={classes.expandableList}
              >
                {notAddedSubjects.map((subject) => (
                  <SubjectCard key={subject.id} subject={subject} onClick={() => subjectEnterClickHandler(subject)} />
                ))}
              </BaseAccordion>
            </Box>
          )}
          {user && (
            <Button className={classes.logoutButton} color="secondary" onClick={() => signOut()}>
              {t("logout")}
            </Button>
          )}
        </Box>
        <LockDialog />
      </CompanyLayout>
    </Box>
  );
};

export default Subjects;
